import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseQuery } from '../../../../../gen-src/queries';
import {
  ApiRequestsContext,
  FeatureFlagsContext,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
  SiteUrlsContext,
  UIActionsContext,
  VerticalFlexContainer
} from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { DatabaseBreadcrumbsUpdater } from '../breadcrumbs/DatabaseBreadcrumbsUpdater';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import { getAnchorCountFromConfig, LineageItemConfig, lineageItemKey, LineageSection, } from '../placePage/LineageConfiguration';
import { DatabaseLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import { DatabaseLineagePaneParams, memoizedGetDatabaseLineageConfiguration } from './DatabaseLineageConfiguration';
import { DatabaseLineageCounts, DatabaseLineageCountsQueryWrapper } from './DatabaseLineageCountsQueryWrapper';
import { DatabasePlacePageHeader } from './DatabasePlacePageHeader';
import { DatabaseQueryWrapper } from './DatabaseQueryWrapper';
import { DatabaseSubplaceParamsProvider } from './DatabaseSubplaceParamsProvider';


export interface DatabasePlacePageProps {
  readonly databaseId: string;
  readonly hideLaunchEditOnMobile?: boolean;
  readonly newFlowFromCatalog?: boolean;
  readonly projectIdForNewDocuments?: string;
}

export class DatabasePlacePage extends React.Component<DatabasePlacePageProps> {
  static override contextType = UIActionsContext
  override context!: React.ContextType<typeof UIActionsContext>

  public override render(): JSX.Element {
    return (
      <SiteUrlsContext.Consumer>
        {urls =>
          <FeatureFlagsContext.Consumer>
            {
              featureFlags =>
                <Switch>
                  {memoizedGetDatabaseLineageConfiguration(
                    featureFlags.metricsDataCatalogEnabled,
                    featureFlags.publishedConnectionsLineageEnabled,
                    featureFlags.lensLineageEnabled).map(lineageSection =>
                      lineageSection.items.map((lineageItem, i) =>
                        <Route
                          key={i}
                          path={lineageItem.path(urls, this.props.databaseId)}
                          render={() => this.renderRoute(lineageItem, lineageSection)}
                        />
                      )
                    )}
                  <Route
                    render={({ location }) => /* location is needed to preserve query parameters */
                      <Switch>
                        <Redirect
                          exact={true}
                          from={urls.catalogDatabasePath(this.props.databaseId)}
                          to={{ ...location, pathname: urls.catalogDatabaseTablesPath(this.props.databaseId) }}
                        />
                        <UnrecognizedUrl
                          backToButtonLabel={Messages.BackToDatabase()}
                          backToButtonUrl={urls.catalogDatabasePath(this.props.databaseId)}
                        />
                      </Switch>
                    }
                  />
                </Switch>
            }
          </FeatureFlagsContext.Consumer>
        }
      </SiteUrlsContext.Consumer>
    );
  }

  public override componentDidMount() {
    this.context.maybeSendDataQualitySubscriptionEvent(this.props.databaseId, 'database');
  }

  public override componentDidUpdate() {
    this.context.maybeSendDataQualitySubscriptionEvent(this.props.databaseId, 'database');
  }

  private readonly renderRoute = (
    lineageItem: LineageItemConfig<DatabaseLineageCounts, DatabaseLineagePaneParams>,
    lineageSection: LineageSection<DatabaseLineageCounts, DatabaseLineagePaneParams>
  ): JSX.Element => {
    return (
      <DatabaseSubplaceParamsProvider>
        {params =>
          <ErrorListProvider>
            <ErrorToasts />
            <DatabaseQueryWrapper databaseId={this.props.databaseId}>
              {database =>
                <DatabaseLineageCountsQueryWrapper
                  databaseId={this.props.databaseId}
                  selectedTableKeys={params.selectedTables}
                >
                  {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                    <>
                      <DatabaseBreadcrumbsUpdater databaseId={this.props.databaseId} databaseName={database.name} />
                      <BrowserTitleUpdater
                        name={database.name}
                        browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_PLACE_ENTITY_NAME}
                      />
                      <VerticalFlexContainer fillFlexParent={true}>
                        <ApiRequestsContext.Consumer>
                          {apiRequests =>
                            <DatabasePlacePageHeader
                              apiRequests={apiRequests}
                              database={database}
                              hideLaunchEditOnMobile={this.props.hideLaunchEditOnMobile}
                              newFlowFromCatalog={this.props.newFlowFromCatalog}
                              projectIdForNewDocuments={this.props.projectIdForNewDocuments}
                            />
                          }
                        </ApiRequestsContext.Consumer>
                        <DatabaseLineageSubplace
                          databaseId={this.props.databaseId}
                          database={database}
                          lineageItem={lineageItem}
                          lineageSection={lineageSection}
                          countsLoading={countsLoading}
                          unfilteredCounts={unfilteredCounts}
                          filteredCounts={filteredCounts}
                          urlSearchValue={params.urlSearchValue}
                          selectedKeys={params.selectedTables}
                          updateSelectionForKey={params.updateTableSelection}
                          clearTableSelection={params.clearTableSelection}
                        />
                      </VerticalFlexContainer>
                    </>
                  }
                </DatabaseLineageCountsQueryWrapper>
              }
            </DatabaseQueryWrapper>
          </ErrorListProvider>
        }
      </DatabaseSubplaceParamsProvider>
    );
  }

}

type Database = Unpacked<DatabaseQuery['databases']>

type DatabaseLineageSubplaceProps = {
  readonly databaseId: string;
  readonly database: Database;
  readonly lineageItem: LineageItemConfig<DatabaseLineageCounts, DatabaseLineagePaneParams>;
  readonly lineageSection: LineageSection<DatabaseLineageCounts, DatabaseLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: DatabaseLineageCounts | null;
  readonly filteredCounts: DatabaseLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearTableSelection: () => void;
} & SelectionProviderChildProps;

class DatabaseLineageSubplace extends React.PureComponent<DatabaseLineageSubplaceProps> {

  public override render(): JSX.Element {
    const selectedTables = Object.keys(this.props.selectedKeys);
    const hasFilter = selectedTables.length > 0;
    const filteredTableId: string | null = hasFilter ? selectedTables[0] : null;
    const lineageCounts = this.props.filteredCounts ? this.props.filteredCounts : this.props.unfilteredCounts;
    return (
      <FeatureFlagsContext.Consumer>
        {
          featureFlags =>
            <DatabaseLineageSubplaceLayout
              activeLineageItemKey={lineageItemKey(this.props)}
              selectedAnchorItemIds={selectedTables}
              pane={this.props.lineageItem.paneFactory({
                title: messageWithOrWithoutCount(
                  lineageCounts && this.props.lineageItem.count(lineageCounts),
                  this.props.lineageItem.messagesForPane
                ),
                database: this.props.database,
                databaseId: this.props.databaseId,
                filteredTableId,
                count: lineageCounts && this.props.lineageItem.count(lineageCounts),
                selectedKeys: this.props.selectedKeys,
                updateSelectionForKey: this.props.updateSelectionForKey,
              })}
              databaseId={this.props.databaseId}
              countsLoading={this.props.countsLoading}
              unfilteredCounts={this.props.unfilteredCounts}
              filteredCounts={this.props.filteredCounts}
              urlSearchValue={this.props.urlSearchValue}
              clearTableSelection={this.props.clearTableSelection}
              anchorName={this.props.database.name}
              anchorCount={getAnchorCountFromConfig(
                memoizedGetDatabaseLineageConfiguration(
                  featureFlags.metricsDataCatalogEnabled,
                  featureFlags.publishedConnectionsLineageEnabled,
                  featureFlags.lensLineageEnabled),
                this.props.unfilteredCounts)}
              filteredOnItemType={hasFilter ? LineageItemType.Database : null}
              filteredItemName={this.props.filteredCounts?.filteredTableName ?? null}
            />
        }
      </FeatureFlagsContext.Consumer>
    );
  }

}
